import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useContentAppearance } from '@confluence/content-appearance/entry-points/ContentAppearanceContext.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { ContentAppearanceType } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { containsNetworkError } from '@confluence/network';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty.ts';
import { useContentType } from '@confluence/page-context';
import { expValEquals } from '@confluence/feature-experiments';

import type { UpdateContentAppearancePropertyBody } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { EditorAppearanceDropdownComponent } from './EditorAppearanceDropdownComponent.tsx';

export const i18n: {
	errorFlagTitle: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	unauthorizedErrorFlagBody: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	networkErrorFlagBody: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	widthChangeFlagTitleNarrow: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	widthChangeFlagTitleWide: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	widthChangeFlagTitleMax: {
		id: string;
		description: string;
		defaultMessage: string;
	};
	widthChangeFlagTitleGeneric: {
		id: string;
		description: string;
		defaultMessage: string;
	};
} = defineMessages({
	errorFlagTitle: {
		id: 'editor-appearance-button.error.flag.title',
		description:
			'Title for the error flag shown when there is a conflict updating the width of the content',
		defaultMessage: 'Unable to update width',
	},
	unauthorizedErrorFlagBody: {
		id: 'editor-appearance-button.unauthorized.error.flag.body',
		description:
			'Body text for the error flag shown when there is unauthorized error updating the width of the content',
		defaultMessage:
			'You don’t have permission to update the width. Contact your admin to request access.',
	},
	networkErrorFlagBody: {
		id: 'editor-appearance-button.network.error.flag.body',
		description: "Message for error flags when an error with a user's internet connection occurs",
		defaultMessage: 'Check your internet connection, refresh, and try again.',
	},
	widthChangeFlagTitleNarrow: {
		id: 'editor-appearance-button.max-width.width-change.flag.description.narrow',
		description: 'Title for the info flag shown after applying narrow width',
		defaultMessage: 'Narrow width applied',
	},
	widthChangeFlagTitleWide: {
		id: 'editor-appearance-button.max-width.width-change.flag.description.wide',
		description: 'Title for the info flag shown after applying wide width',
		defaultMessage: 'Wide width applied',
	},
	widthChangeFlagTitleMax: {
		id: 'editor-appearance-button.max-width.width-change.flag.description.max',
		description: 'Title for the info flag shown after applying max width',
		defaultMessage: 'Max width applied',
	},
	widthChangeFlagTitleGeneric: {
		id: 'editor-appearance-button.max-width.width-change.flag.description.generic',
		description: 'Fallback title for the info flag shown after updating width',
		defaultMessage: 'Width updated',
	},
});

const WIDTH_CHANGE_FLAG_ID = 'editor-appearance-width-change-flag';

const widthChangeFlagMessages: Partial<Record<ContentAppearanceType, MessageDescriptor>> = {
	[ContentAppearanceType.DEFAULT]: i18n.widthChangeFlagTitleNarrow,
	[ContentAppearanceType.FULL_WIDTH]: i18n.widthChangeFlagTitleWide,
	[ContentAppearanceType.MAX]: i18n.widthChangeFlagTitleMax,
};

const getWidthChangeFlagMessage = (appearance: ContentAppearanceType): MessageDescriptor =>
	widthChangeFlagMessages[appearance] ?? i18n.widthChangeFlagTitleGeneric;

export type EditorAppearanceButtonNextProps = {
	contentId: string;
	isStatusDisconnected: boolean;
	flags: FlagsStateContainer;
	disabled?: boolean;
	setIsAppearanceDropdownOpen?: (isOpen: boolean) => void;
	isAppearanceDropdownOpen?: boolean;
};

const EditorAppearanceButtonNextComponent = ({
	contentId,
	isStatusDisconnected,
	flags,
	disabled = false,
	setIsAppearanceDropdownOpen,
	isAppearanceDropdownOpen,
}: EditorAppearanceButtonNextProps) => {
	const intl = useIntl();
	const { contentAppearance, setContentAppearance, contentAppearanceLoading } =
		useContentAppearance();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ collabEditProvider, collabEditInitialized }] = useNativeCollabState();
	const [contentType] = useContentType();
	const { updateContentProperty, processUpdateContentPropertyResult } = useUpdateContentProperty({
		contentId,
		contentType: contentType || 'page',
	});
	const updateContentAppearanceProperty = async (
		params: UpdateContentAppearancePropertyBody,
	): Promise<void> => {
		try {
			const result = await updateContentProperty({
				propertyKey: params.propertyKey,
				propertyValue: params.propertyValue,
				isDelete: false,
				hasExistingProperty: contentAppearance.version > 0,
			});
			const responseData = processUpdateContentPropertyResult(result?.data);
			if (responseData?.success) {
				setContentAppearance({
					appearance: params.propertyValue,
					version: params.versionNumber,
				});
			} else {
				if (responseData?.errors?.[0]) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'failed',
							actionSubject: 'updateContentAppearanceProperty',
							source: 'EditorAppearanceButtonNext',
							attributes: {
								error: responseData.errors[0],
							},
						},
					}).fire();
					const statusCode = responseData.errors[0].extensions?.statusCode;
					if (String(statusCode) === '409') {
						// Not showing flag for 409s since it's not a user error but something that's happening on our end
						return;
					} else {
						const flagBodyText =
							String(statusCode) === '403'
								? i18n.unauthorizedErrorFlagBody
								: i18n.networkErrorFlagBody;
						void flags.showErrorFlag({
							id: 'content-appearance-button-conflict-error-flag',
							title: <FormattedMessage {...i18n.errorFlagTitle} />,
							description: <FormattedMessage {...flagBodyText} />,
						});
					}
				}
			}
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'updateContentAppearanceProperty',
					source: 'EditorAppearanceButtonNext',
					attributes: {
						error,
					},
				},
			}).fire();
			const is409Error = isStatusCodeError(error, '409');
			const is403Error = isUnauthorizedError(error);
			const isNetworkError = containsNetworkError(error);

			// Not showing flag for 409s since it's not a user error but something that's happening on our end
			if (is409Error) {
				markErrorAsHandled(error);
				return;
			}

			if (is403Error || isNetworkError) {
				markErrorAsHandled(error);
				const flagBodyText = is403Error
					? i18n.unauthorizedErrorFlagBody
					: i18n.networkErrorFlagBody;

				void flags.showErrorFlag({
					id: 'content-appearance-button-conflict-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...flagBodyText} />,
				});
			}
		}
	};

	const isDisabled = contentAppearanceLoading || !collabEditInitialized || disabled;

	const handleAppearanceUpdateSuccess = (appearance: ContentAppearanceType) => {
		if (!expValEquals('confluence_width_change_info_flag', 'isEnabled', true)) {
			return;
		}
		const titleMessage = getWidthChangeFlagMessage(appearance);
		const title = intl.formatMessage(titleMessage);

		void flags?.showInfoFlag?.({
			id: WIDTH_CHANGE_FLAG_ID,
			title,
			isAutoDismiss: true,
		});
	};

	return (
		<EditorAppearanceDropdownComponent
			contentId={contentId}
			collabEditProvider={collabEditProvider}
			isDisabled={isDisabled}
			isStatusDisconnected={isStatusDisconnected}
			updateContentAppearanceProperty={updateContentAppearanceProperty}
			contentAppearanceDraft={contentAppearance}
			setContentAppearance={setContentAppearance}
			setIsAppearanceDropdownOpen={setIsAppearanceDropdownOpen}
			isAppearanceDropdownOpen={isAppearanceDropdownOpen}
			onAppearanceUpdateSuccess={
				expValEquals('confluence_width_change_info_flag', 'isEnabled', true)
					? handleAppearanceUpdateSuccess
					: undefined
			}
		/>
	);
};

export const EditorAppearanceButtonNext: React.ComponentType<
	Omit<EditorAppearanceButtonNextProps, 'flags'>
> = withFlags(EditorAppearanceButtonNextComponent);
